/**
 * ActivityCard Component
 *
 * A card component for displaying activity information with an image, title, professional name,
 * location type, session count, and starting price.
 */
import { MapPin } from 'lucide-react';

import { cn, formatStartingPrice } from '../../../../lib/utils';
import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';
import BriefcaseOutline from '../../../icons/BriefcaseOutline';
import CoinLinear from '../../../icons/CoinLinear';
import HomeOutline from '../../../icons/HomeOutline';

export interface ActivityCardProps {
  /**
   * Reference to the main activity image
   */
  imageUrl: string;

  /**
   * Title of the activity
   */
  title: string;

  /**
   * Name of the professional offering this activity
   */
  professionalName: string;

  /**
   * Whether the activity happens at client's home
   */
  isAtHome?: boolean;

  /**
   * Total number of sessions for this activity since last year
   */
  sessionsSinceLastYear?: number;

  /**
   * Starting price for the activity.
   *
   * - `null` / `undefined` → activity has no priced session yet; the price
   *   block is hidden entirely (must NOT be shown as "0 €").
   * - `0` → genuinely free; shows `freeText`.
   * - `> 0` → formatted amount with the `onwardsText` prefix.
   */
  startingPrice?: number | null;

  /**
   * Optional click handler
   */
  onClick?: () => void;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Translated text for "At Home" location
   * @default "At Home"
   */
  atHomeText?: string;

  /**
   * Translated text for "At Venue" location
   * @default "At Venue"
   */
  atVenueText?: string;

  /**
   * Translated prefix for sessions count text
   * @default ""
   */
  sessionsInYearPrefix?: string;

  /**
   * Translated middle text between count and year for sessions text
   * @default " Sessions since "
   */
  sessionsInYearMiddle?: string;

  /**
   * Translated suffix for sessions count text
   * @default ""
   */
  sessionsInYearSuffix?: string;

  /**
   * Translated text for price suffix ("onwards")
   * @default "onwards"
   */
  onwardsText?: string;

  /**
   * Translated label shown instead of "0 €" for free sessions
   * @default "Free"
   */
  freeText?: string;

  /**
   * Locale for currency formatting (e.g. 'fr-FR', 'en-US')
   * @default 'fr-FR'
   */
  locale?: string;
}

/**
 * ActivityCard component for displaying activity information
 *
 * @example
 * ```tsx
 * <ActivityCard
 *   imageUrl="https://example.com/image.jpg"
 *   title="Yoga for Beginners"
 *   professionalName="Jane Doe"
 *   isAtHome={true}
 *   currentYearSessionsCount={22}
 *   startingPrice={40}
 *   onClick={() => console.log('Activity clicked')}
 * />
 * ```
 */
export const ActivityCard = ({
  imageUrl,
  title,
  professionalName,
  isAtHome = false,
  sessionsSinceLastYear,
  startingPrice,
  onClick,
  className,
  atHomeText = 'At Home',
  atVenueText = 'At Venue',
  sessionsInYearPrefix = '',
  sessionsInYearMiddle = ' Sessions since ',
  sessionsInYearSuffix = '',
  onwardsText = 'onwards',
  freeText = 'Free',
  locale = 'fr-FR'
}: ActivityCardProps) => {
  const formattedPrice = formatStartingPrice(startingPrice, { locale, onwardsText, freeText });

  return (
    <div
      className={cn(
        'w-48 bg-card rounded-xl border border-border inline-flex flex-col justify-start items-start overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow',
        className
      )}
      onClick={onClick}
    >
      {/* Image section */}
      <div className="self-stretch h-40 relative">
        <ImageWithFallback src={imageUrl} alt={title} className="w-full h-full object-cover" sizes="192px" />
      </div>

      {/* Content section */}
      <div className="self-stretch p-3 flex flex-col justify-start items-start gap-2">
        {/* Title and professional name */}
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="self-stretch text-foreground text-base font-semibold leading-snug truncate" title={title}>
            {title}
          </div>
          <div className="self-stretch text-muted-foreground text-xs font-medium leading-none">{professionalName}</div>
        </div>

        {/* Activity details */}
        <div className="self-stretch flex flex-col justify-center items-start gap-[5px]">
          {/* Location (At Home/At Venue) */}
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            {isAtHome ? (
              <HomeOutline className="w-4 h-4 text-muted-foreground" />
            ) : (
              <MapPin className="w-4 h-4 text-muted-foreground" />
            )}
            <div className="flex justify-start items-center gap-1">
              <div className="text-muted-foreground text-xs font-medium leading-none">
                {isAtHome ? atHomeText : atVenueText}
              </div>
            </div>
          </div>

          {/* Sessions count - only show if more than 5 */}
          {sessionsSinceLastYear != null && sessionsSinceLastYear > 5 && (
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <BriefcaseOutline className="w-4 h-4 text-muted-foreground" />
              <div className="text-muted-foreground text-xs font-normal">
                {sessionsInYearPrefix}
                {sessionsSinceLastYear}
                {sessionsInYearMiddle}
                {new Date().getFullYear() - 1}
                {sessionsInYearSuffix}
              </div>
            </div>
          )}

          {/* Price — hidden when there is no priced session yet */}
          {formattedPrice && (
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <CoinLinear className="w-4 h-4 text-muted-foreground" />
              <div>
                {formattedPrice.pricePrefix && (
                  <span className="text-muted-foreground text-xs font-normal">{formattedPrice.pricePrefix} </span>
                )}
                <span className="text-primary text-sm font-semibold leading-tight">{formattedPrice.price}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
